
import { useEffect } from 'react';

interface StructuredDataProps {
  type: 'home' | 'empresa' | 'servicios' | 'faq' | 'contacto';
}

const StructuredData = ({ type }: StructuredDataProps) => {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://barcelona.biotrauma.es';
    const scripts: any[] = [];

    const localBusiness = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'BIOTRAUMA BARCELONA',
      image: `${siteUrl}/vite.svg`,
      '@id': siteUrl,
      url: siteUrl,
      telephone: '+34629501941',
      priceRange: '$$',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Barcelona',
        addressLocality: 'Barcelona',
        addressRegion: 'Cataluña',
        postalCode: '08001',
        addressCountry: 'ES',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 41.3874,
        longitude: 2.1686,
      },
      areaServed: [
        {
          '@type': 'City',
          name: 'Barcelona',
        },
        {
          '@type': 'AdministrativeArea',
          name: 'Área Metropolitana de Barcelona',
        },
      ],
      openingHoursSpecification: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
        opens: '08:00',
        closes: '20:00',
      },
      sameAs: [
        'https://wa.me/34629501941',
      ],
    };

    scripts.push(localBusiness);

    if (type === 'home') {
      const websiteSchema = {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'BIOTRAUMA BARCELONA',
        url: siteUrl,
        description: 'Empresa especializada en vaciado de pisos, casas, locales y trasteros en Barcelona. Servicio rápido, discreto y con gestión responsable de residuos.',
        publisher: {
          '@type': 'Organization',
          name: 'BIOTRAUMA BARCELONA',
        },
      };
      scripts.push(websiteSchema);

      const breadcrumb = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Inicio',
            item: siteUrl,
          },
        ],
      };
      scripts.push(breadcrumb);
    }

    if (type === 'servicios' || type === 'home') {
      const services = [
        {
          name: 'Vaciado de pisos y casas',
          description: 'Servicio completo de vaciado de viviendas en Barcelona y área metropolitana. Retiramos todos los muebles, electrodomésticos y enseres.',
        },
        {
          name: 'Vaciado de locales comerciales',
          description: 'Vaciado profesional de locales comerciales, oficinas y espacios de negocio en Barcelona.',
        },
        {
          name: 'Vaciado de trasteros y garajes',
          description: 'Limpieza y vaciado completo de trasteros, garajes y espacios de almacenamiento.',
        },
        {
          name: 'Retirada de muebles y electrodomésticos',
          description: 'Servicio de recogida y transporte de muebles y electrodomésticos en Barcelona.',
        },
        {
          name: 'Limpieza final y puesta a punto',
          description: 'Servicio de limpieza profunda tras el vaciado. Dejamos el inmueble impecable.',
        },
        {
          name: 'Gestión de residuos',
          description: 'Gestión responsable de residuos en puntos autorizados y donación de objetos en buen estado.',
        },
      ];

      services.forEach((service) => {
        scripts.push({
          '@context': 'https://schema.org',
          '@type': 'Service',
          serviceType: service.name,
          description: service.description,
          provider: {
            '@type': 'LocalBusiness',
            name: 'BIOTRAUMA BARCELONA',
            url: siteUrl,
          },
          areaServed: {
            '@type': 'City',
            name: 'Barcelona',
          },
        });
      });
    }

    if (type === 'servicios') {
      const breadcrumb = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Inicio',
            item: siteUrl,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Servicios',
            item: `${siteUrl}/servicios`,
          },
        ],
      };
      scripts.push(breadcrumb);
    }

    if (type === 'empresa') {
      const organizationSchema = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'BIOTRAUMA BARCELONA',
        url: siteUrl,
        logo: `${siteUrl}/vite.svg`,
        description: 'Empresa especializada en vaciado de pisos y locales en Barcelona con años de experiencia.',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Barcelona',
          addressRegion: 'Cataluña',
          addressCountry: 'ES',
        },
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+34629501941',
          contactType: 'customer service',
          areaServed: 'ES',
          availableLanguage: 'Spanish',
        },
      };
      scripts.push(organizationSchema);

      const breadcrumb = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Inicio',
            item: siteUrl,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Empresa',
            item: `${siteUrl}/empresa`,
          },
        ],
      };
      scripts.push(breadcrumb);
    }

    if (type === 'faq') {
      scripts.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: '¿Cuánto cuesta vaciar un piso en Barcelona?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'El precio depende de varios factores: metros cuadrados del inmueble, cantidad de enseres a retirar, accesibilidad (planta, ascensor), y ubicación exacta. Realizamos presupuestos personalizados sin compromiso tras ver fotos por WhatsApp o hacer una visita al inmueble. Como referencia, un piso estándar de 70-80m² puede costar entre 400-800€, pero cada caso es único.',
            },
          },
          {
            '@type': 'Question',
            name: '¿Cuál es el servicio mínimo que ofrecéis?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Nuestro servicio mínimo es una furgoneta completa. No realizamos recogidas de objetos sueltos como una sola mesa, silla o electrodoméstico. Este mínimo nos permite mantener precios competitivos y optimizar nuestros recursos.',
            },
          },
          {
            '@type': 'Question',
            name: '¿En cuánto tiempo pueden vaciar un piso?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Depende del tamaño y la cantidad de enseres. Un piso estándar de 70-80m² con mobiliario normal se vacía en 4-6 horas de trabajo. Pisos más grandes de 100-120m² pueden requerir entre 8-12 horas (1-2 días). Casas unifamiliares o locales comerciales grandes pueden necesitar 2-3 días.',
            },
          },
          {
            '@type': 'Question',
            name: '¿Qué hacéis con los muebles y enseres retirados?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Gestionamos todos los residuos de forma responsable en puntos limpios autorizados por el Ayuntamiento de Barcelona. Los objetos en buen estado (muebles, ropa, electrodomésticos funcionales) se donan a entidades sociales y ONGs cuando es posible. Los residuos se clasifican y reciclan según normativa. Entregamos certificado de gestión de residuos si lo necesitas.',
            },
          },
          {
            '@type': 'Question',
            name: '¿Necesito estar presente durante el vaciado?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'No es imprescindible. Muchos clientes nos facilitan las llaves y coordinamos directamente con la portería o administración de fincas. Si prefieres estar presente, también es posible. Lo importante es que tengamos acceso al inmueble y permisos necesarios.',
            },
          },
          {
            '@type': 'Question',
            name: '¿Trabajáis en edificios sin ascensor?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Sí, trabajamos en cualquier tipo de inmueble, con o sin ascensor. Si no hay ascensor, lo tenemos en cuenta en el presupuesto porque requiere más tiempo y esfuerzo físico. Protegemos siempre las escaleras, pasamanos y zonas comunes con materiales adecuados para evitar daños durante la bajada de muebles.',
            },
          },
          {
            '@type': 'Question',
            name: '¿Necesito algún permiso especial de la comunidad?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Normalmente no se necesitan permisos especiales para vaciar un piso, pero sí es recomendable avisar a la portería o administración de fincas con antelación. Nosotros nos coordinamos con ellos para reservar el ascensor si es necesario y asegurar el acceso.',
            },
          },
          {
            '@type': 'Question',
            name: '¿Qué no incluye el servicio de vaciado?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'El servicio estándar no incluye: limpieza profunda (es opcional), obras o reformas, retirada de instalaciones fijas (aire acondicionado, calentadores), residuos peligrosos (amianto, químicos), ni desmontaje de cocinas o baños completos. Si necesitas alguno de estos servicios adicionales, podemos coordinarlo con un presupuesto aparte.',
            },
          },
          {
            '@type': 'Question',
            name: '¿Ofrecéis servicio urgente o en fin de semana?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Sí, ofrecemos servicio urgente según disponibilidad. Si necesitas el vaciado en menos de 48 horas, consúltanos y haremos lo posible por atenderte. También trabajamos en fines de semana si es necesario. Los servicios urgentes o fuera de horario laboral pueden tener un pequeño suplemento.',
            },
          },
          {
            '@type': 'Question',
            name: '¿Cómo se realiza el pago?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Normalmente el pago se realiza al finalizar el servicio, una vez compruebes que todo está correcto. Aceptamos efectivo, transferencia bancaria y Bizum. En vaciados muy grandes podemos solicitar un anticipo del 30-40%. Emitimos factura con todos los datos.',
            },
          },
          {
            '@type': 'Question',
            name: '¿Protegéis las zonas comunes del edificio?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Absolutamente. Protegemos ascensores, escaleras, pasamanos, paredes y suelos con materiales específicos (cartones, mantas, plásticos) para evitar cualquier daño durante el transporte de muebles. Dejamos las zonas comunes tal como las encontramos.',
            },
          },
          {
            '@type': 'Question',
            name: '¿Qué zonas de Barcelona cubrís?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Cubrimos toda Barcelona ciudad (todos los barrios: Eixample, Gràcia, Sants, Sarrià, Les Corts, etc.) y toda el área metropolitana: L\'Hospitalet, Badalona, Santa Coloma, Sant Adrià, Cornellà, El Prat, Sant Cugat, Terrassa, Sabadell, Mataró, Granollers y municipios cercanos.',
            },
          },
        ],
      });

      const breadcrumb = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Inicio',
            item: siteUrl,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Preguntas frecuentes',
            item: `${siteUrl}/faq`,
          },
        ],
      };
      scripts.push(breadcrumb);
    }

    if (type === 'contacto') {
      const contactPage = {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: 'Contacto - BIOTRAUMA BARCELONA',
        url: `${siteUrl}/contacto`,
        description: 'Contacta con BIOTRAUMA BARCELONA para solicitar presupuesto de vaciado de pisos en Barcelona.',
      };
      scripts.push(contactPage);

      const breadcrumb = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Inicio',
            item: siteUrl,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Contacto',
            item: `${siteUrl}/contacto`,
          },
        ],
      };
      scripts.push(breadcrumb);
    }

    scripts.forEach((data) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(data);
      document.head.appendChild(script);
    });

    return () => {
      const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
      existingScripts.forEach((script) => script.remove());
    };
  }, [type]);

  return null;
};

export default StructuredData;
